/**
 * Retry Utility with Exponential Backoff
 *
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * Provides a reusable retry mechanism with:
 * - Exponential backoff with configurable base delay
 * - Jitter to prevent thundering herd
 * - Abort controller support for cancellation
 * - Progress callbacks for UI updates
 * - Custom retry condition evaluation
 * - Type-safe generic implementation
 */

import { logger } from "@/lib/logger";

// ============================================================================
// Types and Interfaces
// ============================================================================

/**
 * Configuration options for retry behavior
 */
interface RetryOptions {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries?: number;
  /** Initial delay in milliseconds before first retry (default: 1000) */
  initialDelayMs?: number;
  /** Maximum delay cap in milliseconds (default: 30000) */
  maxDelayMs?: number;
  /** Whether to add random jitter to delays (default: true) */
  enableJitter?: boolean;
  /** Jitter factor (0-1) - max random addition as fraction of delay (default: 0.3) */
  jitterFactor?: number;
  /** Backoff multiplier for exponential growth (default: 2) */
  backoffMultiplier?: number;
  /** Abort signal for cancellation support */
  signal?: AbortSignal;
  /** Custom function to determine if error is retryable (default: all errors retryable) */
  isRetryable?: (error: unknown) => boolean;
  /** Callback fired before each retry attempt */
  onRetryAttempt?: (
    attempt: number,
    maxRetries: number,
    error: unknown,
  ) => void;
  /** Callback fired when all retries are exhausted */
  onRetriesExhausted?: (error: unknown, attempts: number) => void;
  /** Callback fired on successful completion */
  onSuccess?: (result: unknown, attempts: number) => void;
  /** Operation name for logging (default: 'operation') */
  operationName?: string;
}

/**
 * Result of a retry operation
 */
interface RetryResult<T> {
  /** Whether the operation succeeded */
  success: boolean;
  /** The result data (if successful) */
  data?: T;
  /** The error (if failed) */
  error?: unknown;
  /** Total number of attempts made */
  attempts: number;
  /** Whether operation was cancelled */
  cancelled: boolean;
}

// ============================================================================
// Default Configuration
// ============================================================================

const DEFAULT_OPTIONS: Required<
  Omit<
    RetryOptions,
    | "signal"
    | "onRetryAttempt"
    | "onRetriesExhausted"
    | "onSuccess"
    | "isRetryable"
  >
> = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 30000,
  enableJitter: true,
  jitterFactor: 0.3,
  backoffMultiplier: 2,
  operationName: "operation",
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Calculate delay for a specific retry attempt using exponential backoff
 *
 * Formula: min(initialDelay * multiplier^attempt + jitter, maxDelay)
 *
 * @param attempt - Current attempt number (0-indexed)
 * @param options - Retry configuration options
 * @returns Delay in milliseconds before next retry
 */
const calculateRetryDelay = (
  attempt: number,
  options: Pick<
    RetryOptions,
    | "initialDelayMs"
    | "maxDelayMs"
    | "enableJitter"
    | "jitterFactor"
    | "backoffMultiplier"
  > = {},
): number => {
  const {
    initialDelayMs = DEFAULT_OPTIONS.initialDelayMs,
    maxDelayMs = DEFAULT_OPTIONS.maxDelayMs,
    enableJitter = DEFAULT_OPTIONS.enableJitter,
    jitterFactor = DEFAULT_OPTIONS.jitterFactor,
    backoffMultiplier = DEFAULT_OPTIONS.backoffMultiplier,
  } = options;

  // Calculate base delay with exponential backoff
  const exponentialDelay =
    initialDelayMs * Math.pow(backoffMultiplier, attempt);

  // Add jitter if enabled (random value between 0 and jitterFactor * delay)
  const jitter = enableJitter
    ? Math.random() * jitterFactor * exponentialDelay
    : 0;

  // Calculate total delay and cap at maxDelay
  const totalDelay = Math.min(exponentialDelay + jitter, maxDelayMs);

  return Math.round(totalDelay);
};

/**
 * Default retry condition - all errors are retryable
 */
const defaultIsRetryable = (_error: unknown): boolean => true;

/**
 * Create a delay promise that can be cancelled
 */
const delay = (ms: number, signal?: AbortSignal): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Delay aborted", "AbortError"));
      return;
    }

    const timeoutId = setTimeout(resolve, ms);

    if (signal) {
      const abortHandler = () => {
        clearTimeout(timeoutId);
        reject(new DOMException("Delay aborted", "AbortError"));
      };
      signal.addEventListener("abort", abortHandler, { once: true });

      // Clean up abort listener when timeout completes
      setTimeout(() => {
        signal.removeEventListener("abort", abortHandler);
      }, ms + 1);
    }
  });
};

// ============================================================================
// Main Retry Function
// ============================================================================

/**
 * Execute an async operation with automatic retry on failure
 *
 * Features:
 * - Exponential backoff with configurable delays
 * - Optional jitter to prevent thundering herd
 * - Cancellation support via AbortSignal
 * - Progress callbacks for UI updates
 * - Type-safe generic result
 *
 * @param operation - Async function to execute
 * @param options - Retry configuration options
 * @returns Promise resolving to RetryResult with success/failure info
 *
 * @example
 * // Basic usage
 * const result = await withRetry(
 *   () => fetchLicenseData(paymentIntent),
 *   { maxRetries: 3 }
 * );
 *
 * @example
 * // With progress callbacks
 * const result = await withRetry(
 *   () => api.get('/licenses'),
 *   {
 *     maxRetries: 5,
 *     onRetryAttempt: (attempt, max) => {
 *       setStatus(`Retrying... (${attempt}/${max})`);
 *     },
 *     onRetriesExhausted: (error) => {
 *       setError('Failed after all retries');
 *     }
 *   }
 * );
 *
 * @example
 * // With cancellation
 * const controller = new AbortController();
 * const result = await withRetry(
 *   () => longRunningOperation(),
 *   { signal: controller.signal }
 * );
 * // To cancel: controller.abort();
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {},
): Promise<RetryResult<T>> {
  const {
    maxRetries = DEFAULT_OPTIONS.maxRetries,
    initialDelayMs = DEFAULT_OPTIONS.initialDelayMs,
    maxDelayMs = DEFAULT_OPTIONS.maxDelayMs,
    enableJitter = DEFAULT_OPTIONS.enableJitter,
    jitterFactor = DEFAULT_OPTIONS.jitterFactor,
    backoffMultiplier = DEFAULT_OPTIONS.backoffMultiplier,
    signal,
    isRetryable = defaultIsRetryable,
    onRetryAttempt,
    onRetriesExhausted,
    onSuccess,
    operationName = DEFAULT_OPTIONS.operationName,
  } = options;

  let lastError: unknown;
  let attempts = 0;

  // Total attempts = 1 initial + maxRetries
  const totalAttempts = maxRetries + 1;

  while (attempts < totalAttempts) {
    // Check for cancellation before each attempt
    if (signal?.aborted) {
      logger.debug(
        `${operationName}: Cancelled before attempt ${attempts + 1}`,
      );
      return {
        success: false,
        error: new DOMException("Operation cancelled", "AbortError"),
        attempts,
        cancelled: true,
      };
    }

    attempts++;

    try {
      logger.debug(`${operationName}: Attempt ${attempts}/${totalAttempts}`);
      const result = await operation();

      // Success!
      logger.debug(`${operationName}: Succeeded on attempt ${attempts}`);
      onSuccess?.(result, attempts);

      return {
        success: true,
        data: result,
        attempts,
        cancelled: false,
      };
    } catch (error) {
      lastError = error;

      // Check for AbortError (from signal or fetch abort)
      if (error instanceof DOMException && error.name === "AbortError") {
        logger.debug(`${operationName}: Aborted on attempt ${attempts}`);
        return {
          success: false,
          error,
          attempts,
          cancelled: true,
        };
      }

      // Check if error is retryable
      if (!isRetryable(error)) {
        logger.debug(
          `${operationName}: Non-retryable error on attempt ${attempts}`,
          error,
        );
        onRetriesExhausted?.(error, attempts);
        return {
          success: false,
          error,
          attempts,
          cancelled: false,
        };
      }

      // If we have more attempts, wait and retry
      if (attempts < totalAttempts) {
        const retryDelay = calculateRetryDelay(attempts - 1, {
          initialDelayMs,
          maxDelayMs,
          enableJitter,
          jitterFactor,
          backoffMultiplier,
        });

        logger.debug(
          `${operationName}: Attempt ${attempts} failed, retrying in ${retryDelay}ms`,
          error,
        );

        // Fire retry callback
        onRetryAttempt?.(attempts, maxRetries, error);

        // Wait before next retry (cancellable)
        try {
          await delay(retryDelay, signal);
        } catch (delayError) {
          // Delay was cancelled
          if (
            delayError instanceof DOMException &&
            delayError.name === "AbortError"
          ) {
            logger.debug(`${operationName}: Cancelled during retry delay`);
            return {
              success: false,
              error: lastError,
              attempts,
              cancelled: true,
            };
          }
          throw delayError;
        }
      }
    }
  }

  // All retries exhausted
  logger.warn(`${operationName}: All ${totalAttempts} attempts failed`);
  onRetriesExhausted?.(lastError, attempts);

  return {
    success: false,
    error: lastError,
    attempts,
    cancelled: false,
  };
}



